import { getInfiltrationScore } from "../../hsa/infiltration"
import { IHealthySoilsAssessment, IHsaDrainage, IHsaDrillType, IHsaPrimaryCultivations } from "../../hsa/types"
import { defineDirective, FieldViewModel, IOmniaShell, shell } from "../../omnia"
import { IAnalysisValueColumn, ISoilService } from "../../types"
import { SoilMapContext } from "../map/context"
import { IDashboardContext } from "../types"
import { HsaTableColumns } from "./columns"

export class HealthySoilsAssessmentTableCtrl {

    static create(): ng.IDirective {
        return defineDirective(HealthySoilsAssessmentTableCtrl, "app/soil/dashboard/hsa/hsa.html", {
            context: "="
        });
    }

    public readonly readOnly = this.shell.isReadOnly(p => p.soil)

    private context: IDashboardContext;

    private static settingHsaColumns: string = "hsacolumnsvisibility";

    public map: SoilMapContext;
    public selectedField: FieldViewModel;

    healthySoilsAssessments: IHealthySoilsAssessment[];

    private hsaDrainages: IHsaDrainage[];
    private primaryCultivations: IHsaPrimaryCultivations[];
    private drillTypes: IHsaDrillType[];

    public hsaColumns: HsaTableColumns = new HsaTableColumns();
    private hsaColumnsWatcher: any;


    static $inject = ["$scope", "OmniaSoilService", "OmniaShell"];

    constructor(private $scope: ng.IScope, private svc: ISoilService, private shell: IOmniaShell) {
    }

    $onInit() {

        this.$scope.$watch("vm.context", () => this.setup());

        this.hsaDrainages = this.svc.getHsaDrainage();
        this.primaryCultivations = this.svc.getPrimaryCultivations();
        this.drillTypes = this.svc.getDrillTypes();

    }

    private setup() {

        this.map = null;
        this.selectedField = null;
        this.healthySoilsAssessments = [];

        if (!this.context) return;

        var hsaColumnsVisibility = this.svc.settings.get(HealthySoilsAssessmentTableCtrl.settingHsaColumns);

        if (hsaColumnsVisibility != null) {
            this.hsaColumns.columns.forEach(c => c.visible = hsaColumnsVisibility[c.key ? c.key : c.name]);
        }

        if (!this.hsaColumnsWatcher) {
            this.hsaColumnsWatcher = this.$scope.$watch("vm.serializedHsaColumnsDescription", () => {
                var columns = {};
                this.hsaColumns.columns.forEach(c => columns[c.key ? c.key : c.name] = c.visible);
                this.svc.settings.set(HealthySoilsAssessmentTableCtrl.settingHsaColumns, columns);
            });
        }

        this.map = this.context.map;
        this.selectedField = this.context.selectedField;

        if (this.selectedField) {

            this.healthySoilsAssessments = this.svc.getHealthySoilsAssessments()
                .filter(s => s.Allocations && s.Allocations.filter(a => a.FieldId === this.selectedField.fieldId).length > 0)
                .sort((a1, a2) => {
                    let o = a2.SamplingDate.valueOf() - a1.SamplingDate.valueOf()
                    o = o || (a1.AnalysisNumber || "").localeCompare(a2.AnalysisNumber || "")
                    o = o || (a2.HealthySoilsAssessmentId - a1.HealthySoilsAssessmentId)
                    return o
                })

            if (this.context.selectedZone) {
                let data = <IHealthySoilsAssessment>this.context.selectedZone.zone.Data;
                this.context.selectedHsaId = data ? data.HealthySoilsAssessmentId : null;
            }

        }

    }

    get serializedHsaColumnsDescription(): string {
        return this.hsaColumns.columns.map(c => c.key + "-" + c.visible).join(";");
    }

    private editColumns() {

        this.shell.showComponentAsDialog("omnia-soil-dashboard-columns-popup",
            {
                columns: this.hsaColumns.columns,
                max: 5
            }, {
            backdrop: "static"
        });

    }

    private allocAssessment() {

        this.shell.showComponentAsDialog("omnia-soil-assessment-alloc-popup", this.selectedField, {
            backdrop: "static",
            size: "xlarge"
        }).then(r => {
            this.context.refresh();
        });

    }

    private createAssessment() {

        var assessment: IHealthySoilsAssessment = {
            HealthySoilsAssessmentId: 0,
            SamplingDate: new Date(),
            FirstYearId: this.svc.getSession().yearId,
            HoldingId: this.svc.getSession().holdingId,
            Allocations: this.selectedField ? [{
                HealthySoilAssessmentAllocId: 0,
                HealthySoilAssessmentId: 0,
                FieldId: this.selectedField.fieldId
            }] : []
        };

        this.editAssessment(assessment);

    }

    private editAssessment(assessment: IHealthySoilsAssessment) {

        let options = {
            backdrop: "static",
            size: "xxlarge"
        };

        this.context.showDialog("omnia-soil-assessment-edit-popup", assessment, options);

    }

    unAllocAssessment(assessment: IHealthySoilsAssessment) {

        var msg = "You are about to unallocate this healthy soils assessment from the field " + this.selectedField.description + ". Are you sure ?";

        shell.showDeleteCancel(this.shell, msg, "Unallocate").then(r => {
            if (r) {
                this.svc.unallocHsa(assessment.HealthySoilsAssessmentId, this.selectedField.fieldId)
                    .then(r => this.context.refresh());
            }
        });

    }

    getInfiltrationScore(rate: number): string {
        return getInfiltrationScore(rate);
    }

    getAnalysisValue(assessment: IHealthySoilsAssessment, column: IAnalysisValueColumn): any {

        if (column.name === "InfiltrationRate") {
            if (!assessment.InfiltrationRate) {
                return null;
            }

            return this.getInfiltrationScore(assessment.InfiltrationRate);
        }

        if (column.name === "Capping") {
            return assessment.Capping ? "Yes" : "No";
        }

        if (column.name === "Drainage") {
            if (!assessment.DrainageId) return null;
            return this.hsaDrainages.find(d => d.HsaDrainageId === assessment.DrainageId).Name;
        }

        if (column.name === "PrimaryCultivations") {
            if (!assessment.PrimaryCultivationsId) return null;
            return this.primaryCultivations.find(d => d.HsaPrimaryCultivationsId === assessment.PrimaryCultivationsId).Name;
        }

        if (column.name === "DrillType") {
            if (!assessment.DrillTypeId) return null;
            return this.drillTypes.find(d => d.HsaDrillTypeId === assessment.DrillTypeId).Name;
        }

        return assessment[column.name];

    }

    hasAnalysisValue(assessment: IHealthySoilsAssessment, column: IAnalysisValueColumn): boolean {
        return !!this.getAnalysisValue(assessment, column);
    }

    isHsaSelected(hsa: IHealthySoilsAssessment) {

        if (hsa && this.context) {
            return (hsa.HealthySoilsAssessmentId == this.context.selectedHsaId);
        }
    }

    selectHsa(hsa: IHealthySoilsAssessment) {
        if (this.context) {
            let hsaId = hsa ? hsa.HealthySoilsAssessmentId : null;
            this.context.selectedHsaId = hsaId;
            this.map.selectHsaZone(hsaId);
        }
    }
}
